/**
 * Background Location Service
 * 
 * App Store Compliant Background GPS Tracking
 * - ONLY runs when Trip Mode is ON
 * - Adaptive update frequency (battery-safe)
 * - Queues updates offline
 * - Stops immediately when trip ends
 * 
 * Note: Native background geolocation plugin is loaded dynamically
 * at runtime on iOS/Android only.
 */
import { Capacitor } from '@capacitor/core';

const isNative = Capacitor.isNativePlatform();

// Adaptive update intervals (in milliseconds)
const UPDATE_INTERVALS = {
  UNDERWAY: 15000,      // 15s when moving fast (>5 knots)
  SLOW: 30000,          // 30s when moving slow (0.5-5 knots)
  ANCHORED: 120000,     // 2 min when stationary
};

// Speed thresholds (in knots)
const SPEED_THRESHOLDS = {
  UNDERWAY: 5,
  SLOW: 0.5,
};

// Queue for offline updates
let offlineQueue = [];
let isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

// Track watcher state
let watcherId = null;
let isTracking = false;
let currentSpeed = 0;
let lastUpdateTime = 0;
let onLocationCallback = null;
let onErrorCallback = null;

// Persistent ("Always share") background watcher state - independent of trips
let persistentWatcherId = null;
let persistentActive = false;
let onPersistentLocationCallback = null;

// Native plugin reference
let nativePlugin = null;

// ---------------------------------------------------------------------------
// Prominent-disclosure consent gate (Google Play / Apple policy compliance).
// The OS background-location permission must NEVER be requested before the
// user has seen the prominent disclosure and tapped "Allow".
// ---------------------------------------------------------------------------
const BG_CONSENT_KEY = 'boat360_bg_location_consent';

export const hasBackgroundLocationConsent = () => {
  try {
    return localStorage.getItem(BG_CONSENT_KEY) === 'granted';
  } catch (e) {
    return false;
  }
};

export const setBackgroundLocationConsent = (granted) => {
  try {
    localStorage.setItem(BG_CONSENT_KEY, granted ? 'granted' : 'denied');
  } catch (e) {
    /* ignore */
  }
};

/**
 * Initialize online/offline listeners
 */
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    isOnline = true;
    flushOfflineQueue();
  });
  window.addEventListener('offline', () => {
    isOnline = false;
  });
}

/**
 * Calculate update interval based on current speed
 */
const getUpdateInterval = (speedKnots) => {
  if (speedKnots > SPEED_THRESHOLDS.UNDERWAY) {
    return UPDATE_INTERVALS.UNDERWAY;
  } else if (speedKnots > SPEED_THRESHOLDS.SLOW) {
    return UPDATE_INTERVALS.SLOW;
  }
  return UPDATE_INTERVALS.ANCHORED;
};

/**
 * Convert m/s to knots
 */
const msToKnots = (ms) => (ms || 0) * 1.94384;

/**
 * Queue a location update for later sending
 */
const queueUpdate = (locationData) => {
  offlineQueue.push({
    ...locationData,
    queued_at: new Date().toISOString(),
  });
  
  // Keep queue bounded (max 1000 updates)
  if (offlineQueue.length > 1000) {
    offlineQueue = offlineQueue.slice(-1000);
  }
  
  console.log(`[BackgroundLocation] Queued update. Queue size: ${offlineQueue.length}`);
};

/**
 * Flush offline queue when connection is restored
 */
const flushOfflineQueue = async () => {
  if (offlineQueue.length === 0 || !isOnline) return;
  
  console.log(`[BackgroundLocation] Flushing ${offlineQueue.length} queued updates`);
  
  const updates = [...offlineQueue];
  offlineQueue = [];
  
  // Send each update with the callback
  for (const update of updates) {
    try {
      if (onLocationCallback) {
        await onLocationCallback(update);
      }
    } catch (error) {
      console.error('[BackgroundLocation] Failed to flush update:', error);
      // Re-queue failed update
      offlineQueue.push(update);
    }
  }
};

/**
 * Process a location update
 */
const processLocation = async (location) => {
  const speedKnots = msToKnots(location.coords.speed);
  currentSpeed = speedKnots;
  
  const now = Date.now();
  const interval = getUpdateInterval(speedKnots);
  
  // Check if enough time has passed for this speed
  if (lastUpdateTime && (now - lastUpdateTime) < interval) {
    return; // Skip this update (throttled)
  }
  
  lastUpdateTime = now;
  
  const locationData = {
    lat: location.coords.latitude,
    lon: location.coords.longitude,
    speed: speedKnots,
    heading: location.coords.heading || 0,
    accuracy: location.coords.accuracy || 0,
    altitude: location.coords.altitude,
    source: 'bg', // Background source indicator
    timestamp: new Date().toISOString(),
  };
  
  console.log(`[BackgroundLocation] Update: ${locationData.lat.toFixed(4)}, ${locationData.lon.toFixed(4)} @ ${speedKnots.toFixed(1)}kts`);
  
  if (isOnline && onLocationCallback) {
    try {
      await onLocationCallback(locationData);
    } catch (error) {
      console.error('[BackgroundLocation] Failed to send update, queuing:', error);
      queueUpdate(locationData);
    }
  } else {
    queueUpdate(locationData);
  }
};

// Throttle timer for persistent (always-on) updates
let lastPersistentUpdate = 0;

/**
 * Process a location update coming from the persistent ("Always share") watcher.
 * Normalizes both native ({latitude, longitude}) and web ({coords}) shapes.
 */
const processPersistentLocation = async (location) => {
  const coords = location.coords || location;
  const lat = coords.latitude;
  const lon = coords.longitude;
  if (typeof lat !== 'number' || typeof lon !== 'number') return;

  const speedKnots = msToKnots(coords.speed);
  currentSpeed = speedKnots;

  const now = Date.now();
  const interval = getUpdateInterval(speedKnots);
  if (lastPersistentUpdate && (now - lastPersistentUpdate) < interval) {
    return; // throttled
  }
  lastPersistentUpdate = now;

  const locationData = {
    lat,
    lon,
    speed: speedKnots,
    heading: coords.heading ?? coords.bearing ?? 0,
    accuracy: coords.accuracy || 0,
    source: 'bg',
    timestamp: new Date().toISOString(),
  };

  console.log(`[BackgroundLocation] Persistent update: ${lat.toFixed(4)}, ${lon.toFixed(4)} @ ${speedKnots.toFixed(1)}kts`);

  if (onPersistentLocationCallback) {
    try {
      await onPersistentLocationCallback(locationData);
    } catch (error) {
      console.error('[BackgroundLocation] Persistent update failed:', error);
    }
  }
};


/**
 * Load native background geolocation plugin
 */
const loadNativePlugin = async () => {
  if (!isNative) return null;
  if (nativePlugin) return nativePlugin;
  
  try {
    // This will only succeed in native builds where the plugin is registered
    const { registerPlugin } = await import('@capacitor/core');
    nativePlugin = registerPlugin('BackgroundGeolocation');
    console.log('[BackgroundLocation] Native plugin loaded');
    return nativePlugin;
  } catch (error) {
    console.log('[BackgroundLocation] Native plugin not available, using web fallback');
    return null;
  }
};

/**
 * Background Location Service
 */
export const BackgroundLocationService = {
  /**
   * Check if background location is supported
   */
  isSupported() {
    return isNative;
  },

  /**
   * Check if currently tracking
   */
  isTracking() {
    return isTracking;
  },

  /**
   * Get current speed (knots)
   */
  getCurrentSpeed() {
    return currentSpeed;
  },

  /**
   * Get offline queue size
   */
  getQueueSize() {
    return offlineQueue.length;
  },

  /**
   * Request background location permissions
   */
  async requestPermissions() {
    if (!isNative) {
      console.log('[BackgroundLocation] Not native platform, using web geolocation');
      return { granted: true };
    }

    try {
      const plugin = await loadNativePlugin();
      if (plugin && plugin.requestPermissions) {
        const status = await plugin.requestPermissions();
        console.log('[BackgroundLocation] Permission status:', status);
        return { granted: status.location === 'granted' || status.backgroundLocation === 'granted' };
      }
      return { granted: false };
    } catch (error) {
      console.error('[BackgroundLocation] Permission error:', error);
      return { granted: false, error };
    }
  },

  /**
   * Start background tracking for Trip Mode
   * @param callbacks { onLocation, onError }
   */
  async startTracking(callbacks = {}) {
    if (isTracking) {
      console.log('[BackgroundLocation] Already tracking');
      return true;
    }

    onLocationCallback = callbacks.onLocation;
    onErrorCallback = callbacks.onError;

    // Always use web-based tracking (works on both web and in WebView)
    console.log('[BackgroundLocation] Starting web-based tracking');
    
    if (!navigator.geolocation) {
      console.error('[BackgroundLocation] Geolocation not supported');
      return false;
    }

    watcherId = navigator.geolocation.watchPosition(
      (position) => processLocation(position),
      (error) => {
        console.error('[BackgroundLocation] Watch error:', error);
        onErrorCallback?.(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 30000,
        maximumAge: 5000,
      }
    );

    isTracking = true;
    console.log('[BackgroundLocation] Web tracking started');
    
    // Try to also start native background tracking ONLY if the user has
    // consented via the prominent disclosure (Play/Apple policy requirement).
    if (isNative && hasBackgroundLocationConsent()) {
      try {
        const plugin = await loadNativePlugin();
        if (plugin && plugin.addWatcher) {
          await plugin.addWatcher(
            {
              backgroundMessage: 'Boat 360 is tracking your trip',
              backgroundTitle: 'Trip Tracking Active',
              requestPermissions: true,
              stale: false,
              distanceFilter: 10,
            },
            (location, error) => {
              if (error) {
                console.error('[BackgroundLocation] Native watcher error:', error);
                return;
              }
              if (location) {
                processLocation(location);
              }
            }
          );
          console.log('[BackgroundLocation] Native background tracking enabled');
        }
      } catch (error) {
        console.log('[BackgroundLocation] Native background not available:', error.message);
      }
    }
    
    return true;
  },

  /**
   * Stop background tracking when Trip ends
   */
  async stopTracking() {
    if (!isTracking) {
      console.log('[BackgroundLocation] Not currently tracking');
      return true;
    }

    console.log('[BackgroundLocation] Stopping tracking...');

    // Stop web tracking
    if (watcherId !== null) {
      navigator.geolocation.clearWatch(watcherId);
      watcherId = null;
    }
    
    // Stop native tracking if running
    if (isNative && nativePlugin && nativePlugin.removeWatcher) {
      try {
        await nativePlugin.removeWatcher({ id: watcherId });
      } catch (error) {
        console.log('[BackgroundLocation] Error stopping native tracking:', error.message);
      }
    }
    
    isTracking = false;
    console.log('[BackgroundLocation] Tracking stopped');
    
    // Flush any remaining queued updates
    await flushOfflineQueue();
    
    return true;
  },

  /**
   * Whether the persistent ("Always share") watcher is active
   */
  isPersistentActive() {
    return persistentActive;
  },

  /**
   * Start persistent background location sharing, independent of any trip.
   * Life360-style: keeps the native background watcher alive so crew stay
   * visible even when the phone is locked and no trip is active.
   * Requires prior background-location consent (prominent disclosure).
   * @param callbacks { onLocation }
   */
  async startPersistentTracking(callbacks = {}) {
    onPersistentLocationCallback = callbacks.onLocation || onPersistentLocationCallback;

    if (!isNative) {
      // On web the foreground watcher in AppContext already handles updates;
      // browsers cannot keep a background watcher alive when minimized.
      console.log('[BackgroundLocation] Persistent tracking is native-only');
      return false;
    }
    if (!hasBackgroundLocationConsent()) {
      console.log('[BackgroundLocation] Persistent tracking requires background consent');
      return false;
    }
    if (persistentActive) {
      return true;
    }

    try {
      const plugin = await loadNativePlugin();
      if (plugin && plugin.addWatcher) {
        persistentWatcherId = await plugin.addWatcher(
          {
            backgroundMessage: 'Boat 360 is sharing your location with your crew',
            backgroundTitle: 'Location Sharing Active',
            requestPermissions: true,
            stale: false,
            distanceFilter: 20,
          },
          (location, error) => {
            if (error) {
              console.error('[BackgroundLocation] Persistent watcher error:', error);
              return;
            }
            if (location) {
              processPersistentLocation(location);
            }
          }
        );
        persistentActive = true;
        console.log('[BackgroundLocation] Persistent background sharing enabled');
        return true;
      }
    } catch (error) {
      console.log('[BackgroundLocation] Persistent tracking not available:', error.message);
    }
    return false;
  },

  /**
   * Stop persistent background location sharing
   */
  async stopPersistentTracking() {
    if (!persistentActive) {
      return true;
    }
    if (isNative && nativePlugin && nativePlugin.removeWatcher && persistentWatcherId) {
      try {
        await nativePlugin.removeWatcher({ id: persistentWatcherId });
      } catch (error) {
        console.log('[BackgroundLocation] Error stopping persistent tracking:', error.message);
      }
    }
    persistentWatcherId = null;
    persistentActive = false;
    console.log('[BackgroundLocation] Persistent background sharing stopped');
    return true;
  },

  /**
   * Open device location settings
   */
  async openSettings() {
    if (!isNative) {
      console.log('[BackgroundLocation] Cannot open settings on web');
      return;
    }

    try {
      const plugin = await loadNativePlugin();
      if (plugin && plugin.openSettings) {
        await plugin.openSettings();
      }
    } catch (error) {
      console.error('[BackgroundLocation] Failed to open settings:', error);
    }
  },
};

export default BackgroundLocationService;
